import { useEffect, useState } from "react";
import useAxios from "../../utils/useAxios";
import { FaSpinner } from "react-icons/fa";
import { hideModal, showModal } from "../../utils/modalUtility";
import Pagination from "../common/pagination";
import Loader from "../common/Loader";
import config from "../../config";
import MediaGalleryModal from "../common/MediaGalleryModal";
import { truncateText } from "../../utils/truncateText";

const BLOG_MODAL_ID = "newBlogModal";
const MEDIA_MODAL_ID = "customModal";

const INITIAL_FORM_DATA = {
  name: "",
  description: "",
  meta_title: "",
  meta_description: "",
  slug: "",
  mediaIds: [],
};

function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentBlogId, setCurrentBlogId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const axiosAPI = useAxios();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [media, setMedia] = useState([]);
  const [mediaPage, setMediaPage] = useState(1); // NEW (Media pagination)
  const [hasMore, setHasMore] = useState(true);

  const [slugError, setSlugError] = useState("");
  const [originalSlug, setOriginalSlug] = useState("");

  const BASE_URL = config?.apiBaseUrl;

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const removeFormData = () => {
    setFormData(INITIAL_FORM_DATA);
  };

  const handleCheckboxChange = (id, file_path) => {
    setFormData((prevFormData) => {
      const currentMediaIds = prevFormData.mediaIds || [];
      const alreadySelected = currentMediaIds.includes(id);

      let updatedMediaIds;

      if (alreadySelected) {
        updatedMediaIds = currentMediaIds.filter((item) => item !== id);
      } else {
        updatedMediaIds = [...currentMediaIds, id];
      }

      let newThumbnailUrl = prevFormData.thumbnail_url;

      if (!alreadySelected && currentMediaIds.length === 0) {
        newThumbnailUrl = file_path;
      }

      if (alreadySelected && id === currentMediaIds[0]) {
        newThumbnailUrl = updatedMediaIds.length > 0 ? null : "";
      }

      return {
        ...prevFormData,
        mediaIds: updatedMediaIds,
        thumbnail_url: newThumbnailUrl,
      };
    });
  };

  async function validateSlugUnique(slug) {
    try {
      const response = await axiosAPI.get(
        `/api/blogs/check-slug-exist/${slug}`,
      );

      if (response.data.success) {
        // Slug is available
        setSlugError(""); // Clear error
        return true; // valid slug
      } else {
        // Slug already exists
        setSlugError(
          response.data.message ||
            "Slug already exists. Please choose a different one.",
        );
        return false; // invalid slug
      }
    } catch (error) {
      console.error("Error checking slug:", error);
      setSlugError("Error checking slug. Please try again.");
      return false; // consider invalid on error
    }
  }

  function handleMediaSubmit() {
    hideModal(MEDIA_MODAL_ID);
  }

  function openMediaLibraryModal() {
    showModal(MEDIA_MODAL_ID);
  }

  const getMedia = async (pageNumber) => {
    try {
      setLoading(true);

      const response = await axiosAPI.get(`/api/media?page=${pageNumber}`);

      const newMedia = response.data.data;

      setMedia((prev) =>
        pageNumber === 1 ? newMedia : [...prev, ...newMedia],
      );

      setHasMore(pageNumber < response.data.totalPages);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Get All Blogs
  const getBlogs = async () => {
    try {
      const response = await axiosAPI.get(`/api/blogs/get-blogs?page=${page}`);
      setBlogs(response.data.blogs);
      setTotalPages(response?.data?.totalPages);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  // Handle Input Changes
  const handleChange = (e) => {
    const { id, value } = e.target;

    if (id === "name") {
      const slug = value.toLowerCase().replace(/\s+/g, "-");
      setFormData({
        ...formData,
        name: value,
        slug: slug,
      });
    } else {
      setFormData({
        ...formData,
        [id]: value,
      });
    }
  };

  // Handle Submit (Add or Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.slug) {
      setSlugError("Slug is required");
      return;
    }

    if (!isEditMode || formData.slug !== originalSlug) {
      const isSlugValid = await validateSlugUnique(formData.slug);
      if (!isSlugValid) {
        setLoading(false); // important
        return;
      }
    }

    if (!formData.name || !formData.description) {
      return window.notifyMessage(
        "Validation Error",
        "Please enter both name and description.",
        "warning",
      );
    }
    setIsLoading(true);

    const payload = {
      name: formData.name,
      description: formData.description,
      meta_title: formData.meta_title,
      meta_description: formData.meta_description,
      slug: formData.slug,
      media_ids: formData.mediaIds,
    };

    console.log("Payload:", payload);

    // setIsLoading(false);
    // return;

    try {
      if (isEditMode) {
        await axiosAPI.put(`/api/blogs/${currentBlogId}`, payload);
        window.notifyMessage("Success", "Blog updated successfully", "success");
      } else {
        await axiosAPI.post("/api/blogs", payload);
        window.notifyMessage("Success", "Blog added successfully", "success");
      }
      hideModal(BLOG_MODAL_ID);
      getBlogs();
      removeFormData();
      setIsEditMode(false);
      closeModal();
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        window.notifyMessage("Error", error.response.data.message, "danger");
      } else {
        window.notifyMessage("Error", "There was an error!", "danger");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Edit Modal Open
  const openEditModal = (id) => {
    setCurrentBlogId(id);
    const selectedBlog = blogs.find((blog) => blog.id === id);
    if (!selectedBlog) return;

    const mediaIdNumbers = selectedBlog.media_ids
      ? selectedBlog.media_ids.split(",").map(Number)
      : [];

    setFormData({
      name: selectedBlog.name,
      description: selectedBlog.description,
      slug: selectedBlog.slug,
      meta_title: selectedBlog.meta_title,
      meta_description: selectedBlog.meta_description,
      mediaIds: mediaIdNumbers,
    });

    setOriginalSlug(selectedBlog.slug);

    setIsEditMode(true);
    showModal(BLOG_MODAL_ID);
  };

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this?");
    if (!isConfirmed) return;
    try {
      await axiosAPI.delete(`/api/blogs/${id}`);
      window.notifyMessage("Success", "Deleted successfully", "success");
      getBlogs();
    } catch (error) {
      console.error("Error deleting:", error);
      window.notifyMessage(
        "Error",
        "An error occurred while deleting.",
        "danger",
      );
    }
  };

  async function changeBlogStatus(e, blogId) {
    const currentStatus = e.target.value;

    // console.log("product  Id", blogId);
    // console.log("Current Status", currentStatus);

    try {
      const isConfirmed = window.confirm(
        "Are you sure you want to change the product card status?",
      );
      if (!isConfirmed) return;

      await axiosAPI.put(`/api/blogs/change-blog-status/${blogId}`, {
        status: currentStatus,
      });

      getBlogs();
      window.notifyMessage(
        "Success",
        "Blog status changed successfully",
        "success",
      );
    } catch (error) {
      console.error("Error changing blog status:", error);
      window.notifyMessage(
        "Error",
        "Failed to change blog status. Please try again.",
        "danger",
      );
    }
  }

  // Close Modal
  const closeModal = () => {
    setIsEditMode(false);
    removeFormData();
    setCurrentBlogId(null);
  };

  const handleLoadMore = () => {
    setMediaPage((prev) => prev + 1);
  };

  const openAddModal = () => {
    setIsEditMode(false);
    removeFormData();
    setSlugError("");
    showModal(BLOG_MODAL_ID);
  };

  useEffect(() => {
    getMedia(mediaPage);
  }, [mediaPage]);

  useEffect(() => {
    getBlogs();
  }, [page]);

  return (
    <div className="container">
      <div className="page-inner">
        <div className="d-flex justify-content-end align-items-end flex-column flex-md-row">
          <div>
            <button
              type="button"
              className="btn btn-dark btn-round mb-4"
              onClick={() => openAddModal()}
            >
              Add Blog
            </button>
          </div>
        </div>

        {/* Modal */}
        <div
          className="modal fade"
          id={BLOG_MODAL_ID}
          tabIndex="-1"
          aria-labelledby="newBlogModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newBlogModalLabel">
                  {isEditMode ? "Edit Blog" : "Add Blog"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  {/* Blog Name */}
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      placeholder="Enter blog name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Blog Slug */}
                  <div className="mb-3">
                    <label htmlFor="slug" className="form-label">
                      Slug
                    </label>
                    <input
                      type="text"
                      className={`form-control ${slugError ? "is-invalid" : ""}`}
                      id="slug"
                      placeholder="Enter blog slug"
                      value={formData.slug}
                      onChange={handleChange}
                    />
                    {slugError && (
                      <div className="invalid-feedback">{slugError}</div>
                    )}
                  </div>

                  {/* Blog Description */}
                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      placeholder="Enter blog description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={4}
                    />
                  </div>

                  {/* Meta Title */}
                  <div className="mb-3">
                    <label htmlFor="meta_title" className="form-label">
                      Meta Title
                    </label>
                    <textarea
                      className="form-control"
                      id="meta_title"
                      placeholder="Enter blog meta title"
                      value={formData.meta_title}
                      onChange={handleChange}
                      rows={4}
                    />
                  </div>

                  {/* Meta Description */}
                  <div className="mb-3">
                    <label htmlFor="meta_description" className="form-label">
                      Meta Description
                    </label>
                    <textarea
                      className="form-control"
                      id="meta_description"
                      placeholder="Enter blog meta description"
                      value={formData.meta_description}
                      onChange={handleChange}
                      rows={4}
                    />
                  </div>

                  <div className="mb-3 d-flex flex-column gap-2">
                    <h6 className="mb-0">
                      Total Media selected: {formData.mediaIds?.length || 0}
                    </h6>
                    <button
                      type="button"
                      className="btn btn-secondary btn-border btn-round btn-sm align-self-start"
                      onClick={openMediaLibraryModal}
                    >
                      <i className="fas fa-photo-video me-1"></i>
                      Select Media From Gallery
                    </button>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-round btn-sm"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <FaSpinner className="spinner" spin />
                    ) : isEditMode ? (
                      "Update Blog"
                    ) : (
                      "Add Blog"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Blog Table */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-flex align-items-center">
                  <h4 className="card-title mb-0">
                    All Blogs ({blogs?.length})
                  </h4>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Id</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="text-center">
                            <Loader />
                          </td>
                        </tr>
                      ) : blogs?.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No Data Found
                          </td>
                        </tr>
                      ) : (
                        blogs?.map((blog, index) => {
                          return (
                            <tr key={blog.id}>
                              <td>{index + 1}</td>
                              <td>{blog.name}</td>
                              <td>{truncateText(blog.description, 50)}</td>
                              <td>
                                <select
                                  className="form-select"
                                  value={blog.is_active_or_inactive}
                                  onChange={(e) => changeBlogStatus(e, blog.id)}
                                >
                                  <option value="YES">Yes</option>
                                  <option value="NO">No</option>
                                </select>
                              </td>

                              <td>
                                <button
                                  className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                  onClick={() => openEditModal(blog.id)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                  onClick={() => handleDelete(blog.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <Pagination totalPages={totalPages} setPage={setPage} page={page} />
          </div>
        </div>

        <MediaGalleryModal
          id={MEDIA_MODAL_ID}
          title="Media Library"
          media={media}
          selectedIds={formData.mediaIds || []}
          BASE_URL={BASE_URL}
          hasMore={hasMore}
          loading={loading}
          onCheckboxChange={(item) =>
            handleCheckboxChange(item.id, item.file_path)
          }
          onLoadMore={handleLoadMore}
          onSubmit={handleMediaSubmit}
        />
      </div>
    </div>
  );
}

export default Blog;
